/**
 * Credits, as the UI reasons about them. Pure and dependency-free so it can be
 * tested under CRA's jest (see frontend-pure-logic-own-module).
 *
 * Three jobs:
 *   - read a 402 into a `Paywall` the notice can render as an offer
 *   - turn a balance into a sentence ("312 credits · about 2 digs · resets Oct 6")
 *   - keep the balance current from a `credits` websocket event
 */
import type {
  BillingInfo,
  CreditsBalance,
  CreditsWebSocketEvent,
  Paywall,
  PriceBook,
  Product,
} from '../types';

export const EMPTY_CREDITS: CreditsBalance = {
  plan_credits: 0,
  bank_credits: 0,
  balance: 0,
  allowance: 0,
  period_start: null,
  period_end: null,
  overage_allowed: false,
  overage_cap: null,
  overage_credits: 0,
};

export const EMPTY_PRICE_BOOK: PriceBook = {
  version: 0,
  usd_per_credit: 0.01,
  llm_rates: {},
  network_rates: {},
  byok_llm_multiplier: 0.3,
  byok_network_multiplier: 0.3,
};

/** What `EntitlementContext` holds when the fetch failed: nothing held, nothing
 *  to spend, no prices. Fails closed-but-quiet, like the products list. */
export const EMPTY_BILLING: BillingInfo = {
  products: [],
  available: [],
  catalog: {},
  credits: EMPTY_CREDITS,
  price_book: EMPTY_PRICE_BOOK,
  plans: {},
  usage: {},
  sellables: [],
  has_stripe_customer: false,
};

/** A typical dig: ~15 persona turns of ~10k input / 2k output tokens on the
 *  standard tier, and ~20 lookups. Only used to say "about N digs" — the
 *  server meters real tokens and calls. */
const TYPICAL_DIG = {
  input_tokens: 15 * 10_000,
  output_tokens: 15 * 2_000,
  calls: { LOOKUP_SERP: 8, LOOKUP_PAGE: 10, LOOKUP_DEMAND: 2 } as Record<string, number>,
};

/** Credits for a batch of tokens at a tier's rate (cached input ignored). */
export function llmCredits(inputTokens: number, outputTokens: number, book: PriceBook, tier = 'standard'): number {
  const r = book.llm_rates[tier] ?? book.llm_rates.standard;
  if (!r) return 0;
  return (inputTokens / 1000) * r.input_per_1k + (outputTokens / 1000) * r.output_per_1k;
}

export function digCost(book: PriceBook): number {
  const llm = llmCredits(TYPICAL_DIG.input_tokens, TYPICAL_DIG.output_tokens, book);
  const network = Object.entries(TYPICAL_DIG.calls).reduce(
    (sum, [kind, n]) => sum + n * (book.network_rates[kind] ?? 0),
    0,
  );
  return llm + network;
}

/** How many typical digs the balance funds. 0 at zero; null when the book
 *  prices nothing (the estimate would be meaningless). */
export function digsAffordable(balance: number, book: PriceBook): number | null {
  const cost = digCost(book);
  if (cost <= 0) return null;
  if (balance <= 0) return 0;
  return Math.floor(balance / cost);
}

/** OUTPUT_SHARE in jcseo/billing/pricing.py: a turn's tokens are mostly the
 *  brief going in, not the answer coming out. */
const OUTPUT_SHARE = 0.2;

/** How many tokens the balance funds, at the blended standard rate — the
 *  frontend's copy of `pricing.tokens_fundable`.
 *
 *  It has to be a copy because the SERVER SILENTLY CLAMPS a run to this
 *  number. Ask for a million on a balance that funds thirty thousand and the
 *  run starts, does four turns, ends "spent the budget", and nothing on the
 *  page ever mentioned the million you did not get. Knowing the ceiling here
 *  is what lets the picker stop offering it. Null when the book prices
 *  nothing, or the tier is free — then there is no ceiling to draw.
 */
export function tokensFundable(balance: number, book: PriceBook): number | null {
  const r = book.llm_rates.standard;
  if (!r) return null;
  const perToken = ((1 - OUTPUT_SHARE) * r.input_per_1k + OUTPUT_SHARE * r.output_per_1k) / 1000;
  if (perToken <= 0) return null;
  return Math.max(0, Math.floor(balance / perToken));
}

export function formatCredits(n: number): string {
  return Math.round(n).toLocaleString();
}

/** "Oct 6" for a reset date, null when there is no period. */
export function resetsOn(periodEnd: string | null | undefined, now = new Date()): string | null {
  if (!periodEnd) return null;
  const d = new Date(periodEnd);
  if (Number.isNaN(d.getTime())) return null;
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** The one-line summary the boardroom chip shows. */
export function creditsLine(credits: CreditsBalance, book: PriceBook): string {
  // A contract past its allowance: say how far, not "-340 credits".
  if (credits.balance < 0 && credits.overage_allowed) {
    const parts = [`${formatCredits(credits.overage_credits)} credits over your allowance`];
    if (credits.overage_cap !== null) parts.push(`up to ${formatCredits(credits.overage_cap)} allowed`);
    const reset = resetsOn(credits.period_end);
    if (reset) parts.push(`resets ${reset}`);
    return parts.join(' · ');
  }
  const parts = [`${formatCredits(Math.max(0, credits.balance))} credits`];
  const digs = digsAffordable(credits.balance, book);
  if (digs !== null) parts.push(digs === 0 ? 'no digs left' : `about ${digs} dig${digs === 1 ? '' : 's'}`);
  const reset = resetsOn(credits.period_end);
  if (reset) parts.push(`resets ${reset}`);
  return parts.join(' · ');
}

/** Fold a `credits` event into the balance. Everything but the period comes
 *  on the event; the allowance and period start are kept. */
export function applyCreditsEvent(prev: CreditsBalance, ev: CreditsWebSocketEvent): CreditsBalance {
  return {
    ...prev,
    balance: ev.balance,
    plan_credits: ev.plan_credits,
    bank_credits: ev.bank_credits,
    period_end: ev.period_end === undefined ? prev.period_end : ev.period_end,
  };
}

const PRODUCTS: Product[] = ['BEATS', 'PUBLISH', 'BOARDROOM'];

function asProduct(value: unknown): Product | null {
  return typeof value === 'string' && (PRODUCTS as string[]).includes(value)
    ? (value as Product)
    : null;
}

/** Read a 402's detail into a Paywall, or null when the error is something
 *  else. Works on an `ApiError` (status + detail) and on the bare detail. */
export function paywallOf(err: unknown): Paywall | null {
  if (!err || typeof err !== 'object') return null;
  const status = (err as { status?: unknown }).status;
  const detail = 'detail' in (err as object) ? (err as { detail?: unknown }).detail : err;
  if (status !== undefined && status !== 402) return null;
  if (!detail || typeof detail !== 'object') return null;
  const d = detail as Record<string, unknown>;
  switch (d.error) {
    case 'product_required': {
      const products = Array.isArray(d.products)
        ? d.products.map(asProduct).filter((p): p is Product => p !== null)
        : [asProduct(d.product)].filter((p): p is Product => p !== null);
      return { kind: 'product', products };
    }
    case 'product_retired':
      return { kind: 'retired', product: asProduct(d.product) };
    case 'credits_exhausted':
      return {
        kind: 'credits',
        balance: typeof d.balance === 'number' ? d.balance : 0,
        period_end: typeof d.period_end === 'string' ? d.period_end : null,
      };
    case 'plan_limit':
      return {
        kind: 'plan',
        limit: String(d.limit ?? ''),
        plan: String(d.plan ?? 'free'),
        max: typeof d.max === 'number' ? d.max : null,
        product: asProduct(d.product),
      };
    default:
      return null;
  }
}

const LIMIT_COPY: Record<string, (max: number | null, plan: string) => string> = {
  max_rooms: (max, plan) =>
    `The ${plan} plan allows ${max ?? 'unlimited'} open room${max === 1 ? '' : 's'}. Archive one, or move up a plan.`,
  max_personas_per_room: (max, plan) =>
    `The ${plan} plan seats ${max ?? 'unlimited'} persona${max === 1 ? '' : 's'} in a room.`,
  byok_allowed: (_max, plan) =>
    `Bringing your own model, crawler or search key is not part of the ${plan} plan.`,
};

/** The sentence for a paywall, and whether the fix is the billing page. */
export function paywallCopy(p: Paywall, productName: (product: Product) => string): string {
  switch (p.kind) {
    case 'product': {
      const names = p.products.map(productName);
      return names.length
        ? `${names.join(' and ')} ${names.length === 1 ? "isn't" : "aren't"} part of your plan yet.`
        : "This isn't part of your plan yet.";
    }
    case 'retired':
      return `${p.product ? productName(p.product) : 'This product'} has been retired. What you made in it is still on the artifacts shelf.`;
    case 'credits': {
      const reset = resetsOn(p.period_end);
      return reset
        ? `You're out of credits. Your monthly credits reset ${reset}.`
        : "You're out of credits.";
    }
    case 'plan': {
      const plan = p.plan.charAt(0).toUpperCase() + p.plan.slice(1);
      const copy = LIMIT_COPY[p.limit];
      return copy ? copy(p.max, plan) : `That is past what the ${plan} plan allows.`;
    }
  }
}
